import React, { Suspense } from 'react';
import { Router } from 'react-router-dom';
import MomentUtils from '@date-io/moment';
import { Provider as StoreProvider } from 'react-redux';
import { ThemeProvider } from '@material-ui/styles';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import { renderRoutes } from 'react-router-config';
import { SnackbarProvider } from 'notistack';
import { I18nextProvider } from 'react-i18next';

import theme from './theme';
import SaveStatus from 'components/SaveStatus';
import TopNotices from 'components/TopNotices';
import { configureStore } from './store';
import routes from './routes';
import {
  GoogleAnalytics,
  NewVersionNotice,
  Notifier,
  ScrollReset,
  SessionRefresh,
} from './components';
import './mixins/chartjs';
import './mixins/moment';
import './mixins/validate';
import './mixins/prismjs';
import './assets/scss/index.scss';

import {
  errorRequestHandler,
  history,
  whenSessionExpires,
} from 'utils';
import i18n from './i18n';
import ErrorBoundary from './components/Error';

const store = configureStore();

// Any request with a token answered 401 ends the session — on the login page,
// with the one «session expired» message (utils/Axios/axios.js, 21 Oct 2026).
whenSessionExpires(() => store.dispatch(errorRequestHandler({ status: 401, data: {} })));

const App = () => {
  return (
    <ErrorBoundary>
      <StoreProvider store={store}>
        <ThemeProvider theme={theme}>
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <SnackbarProvider maxSnack={3}>
              <I18nextProvider i18n={i18n}>
                <Router history={history}>
                  <Notifier />
                  <SaveStatus />
                  <TopNotices />
                  <ScrollReset />
                  <SessionRefresh />
                  <NewVersionNotice />
                  <GoogleAnalytics />
                  {/*
                    Every route component is lazy(), and the two PUBLIC ones —
                    /display and /display/:token — are deliberately outside the
                    layouts, which is where the only other Suspense boundaries
                    live. Without one here React throws "a component suspended
                    while rendering, but no fallback UI was specified" the
                    moment a screen opens its URL, so the public display could
                    never render at all.

                    Deliberately bare rather than a spinner: this is what a
                    television shows for the instant a chunk is loading, and a
                    progress bar on a black wall-mounted screen reads as a
                    fault. The layouts keep their own inner boundaries for the
                    authenticated app.
                  */}
                  <Suspense fallback={null}>{renderRoutes(routes)}</Suspense>
                </Router>
              </I18nextProvider>
            </SnackbarProvider>
          </MuiPickersUtilsProvider>
        </ThemeProvider>
      </StoreProvider>
    </ErrorBoundary>
  );
};

export default App;
